import axios from 'axios';
import { Globals } from '@/modules/Globals';
import type { IReservationTableBatchEntry } from '@/domain/reservations/reservation-tables-draft';
import type { IOrder } from '@/interfaces/IOrder';
import type { ITable } from '@/interfaces/ITable';
import type {
  IReservationDetail,
  IReservationUpcoming,
  IReservationsDay,
  IReservationsRange,
  IReservationsUpcoming,
  TReservationSource,
  TReservationStatus,
} from '@/interfaces/IReservation';
import type { IReservationSlots } from '@/interfaces/IReservationSlots';
import type { IReservationWaitlist } from '@/interfaces/IReservationWaitlist';
import type {
  IReservationGuest,
  IReservationGuestDetail,
  IReservationGuests,
} from '@/interfaces/IReservationGuest';
import type {
  IReservationArrivalTime,
  IReservationBookingContext,
  IReservationDateOverride,
  IReservationSettings,
  IReservationSettingsConfiguration,
  IReservationShift,
  IReservationTableGroup,
  TReservationSlotInterval,
} from '@/interfaces/IReservationSettings';
import type { IReservationStats } from '@/interfaces/IReservationStats';
import { withShiftInterval } from '@/domain/reservations/reservation-shifts';
import type { TShiftFromApi } from '@/domain/reservations/reservation-shifts';

const apiUrl = Globals.apiPath + '/reservations';

export interface ICreateReservationDto {
  restaurantId: number;
  branchId: number;
  guestName: string;
  guestPhone?: string | null;
  guestEmail?: string | null;
  partySize: number;
  /** ISO instant including Z or an explicit offset; never a bare datetime-local value. */
  startsAt: string;
  durationMin?: number;
  customerId?: number | null;
  sectorId?: number | null;
  tableIds?: number[];
  notesGuest?: string | null;
  notesInternal?: string | null;
  allergies?: string | null;
  occasion?: string | null;
  tags?: string[] | null;
  force?: boolean;
  collectDeposit?: boolean;
  source?: 'staff' | 'phone';
}

export interface IUpdateReservationDto
  extends Partial<
    Omit<ICreateReservationDto, 'restaurantId' | 'branchId' | 'collectDeposit'>
  > {
  notifyGuest?: boolean;
}

export interface IChangeReservationStatusDto {
  status: TReservationStatus;
  reason?: string;
  tableIds?: number[];
  refundDeposit?: boolean;
}

export interface ISetReservationTablesDto {
  tableIds: number[];
  force?: boolean;
}

export interface ISeatReservationDto {
  tableIds?: number[];
  attachToOccupied?: boolean;
}

export interface ICreateReservationResult {
  reservation: IReservationDetail;
  paymentUrl?: string;
}

export interface ISeatReservationResult {
  reservation: IReservationDetail;
  tableId: number;
  tableSessionId: string | null;
}

export interface IApplyReservationDepositDto {
  orderIds?: number[];
  /** Required, even when no caja is selected. */
  cajaId: number | null;
}

export interface IApplyReservationDepositResult {
  /** OrdersManage-only actors receive the same allowlist as upcoming. */
  reservation: IReservationDetail | IReservationUpcoming;
  orders: IOrder[];
  unapplied: number;
}

export interface ICreateReservationWalkInDto {
  restaurantId: number;
  branchId: number;
  partySize: number;
  tableIds: number[];
  guestName?: string;
  guestPhone?: string | null;
  attachToOccupied?: boolean;
}

export interface ICreateReservationWaitlistDto {
  restaurantId: number;
  branchId: number;
  kind: 'walk_in';
  guestName: string;
  guestPhone?: string | null;
  partySize: number;
  quotedWaitMin?: number;
  sectorId?: number | null;
  notes?: string | null;
}

export type IUpdateReservationWaitlistDto = Partial<
  Omit<ICreateReservationWaitlistDto, 'restaurantId' | 'branchId' | 'kind'>
>;
export interface ISeatReservationWaitlistDto extends ISeatReservationDto {
  tableIds: number[];
}
export interface ISeatReservationWaitlistResult extends ISeatReservationResult {
  entry: IReservationWaitlist;
}

export interface IUpdateReservationGuestDto {
  reservationNotes?: string | null;
  tags?: string[] | null;
}

export type IUpdateReservationSettingsDto = Partial<
  Omit<
    IReservationSettings,
    'id' | 'restaurantId' | 'branchId' | 'createdAt' | 'updatedAt'
  >
>;

export interface IReservationShiftDto {
  restaurantId: number;
  branchId: number;
  name: string;
  days: number[];
  from: string;
  to: string;
  maxCovers?: number | null;
  /** Omitted = keep the stored list, null = interval grid, list = fixed times. */
  arrivalTimes?: IReservationArrivalTime[] | null;
  /** Omitted = keep the stored interval (the default on create). */
  slotIntervalMin?: TReservationSlotInterval;
  depositRequired?: boolean | null;
  sort?: number;
}

export interface IReservationOverrideDto {
  closed?: boolean;
  onlineClosed?: boolean;
  depositRequired?: boolean | null;
  windows?:
    | {
        from: string;
        to: string;
        maxCovers?: number | null;
        arrivalTimes?: IReservationArrivalTime[] | null;
      }[]
    | null;
  /** Grid step of the custom windows; null = as the regular shifts, omitted = keep. */
  slotIntervalMin?: TReservationSlotInterval | null;
  note?: string | null;
}

export type IReservationTableGroupDto = Omit<
  IReservationTableGroup,
  'id' | 'createdAt' | 'updatedAt'
>;
/** Batch seats/mode write; each entry carries only the fields that change. */
export interface IReservationTablesBatchDto {
  restaurantId: number;
  branchId: number;
  entries: IReservationTableBatchEntry[];
}

export interface IReservationRangeFilters {
  status?: TReservationStatus;
  source?: TReservationSource;
  search?: string;
}

export type TReservationGuestSort =
  | 'name'
  | 'visitsCount'
  | 'noShowsCount'
  | 'cancellationsCount'
  | 'lastVisitAt';
export interface IReservationGuestFilters {
  search?: string;
  page?: number;
  sortBy?: TReservationGuestSort;
  sortDesc?: boolean;
}

export interface IReservationSlotsOptions {
  excludeReservationId?: number;
  durationMin?: number;
}

export interface IReservationActionResult {
  ok: true;
}

function queryString(
  params: Record<string, string | number | boolean | undefined>
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  return query.toString();
}

/** Staff routes. Permission gates belong to callers and are enforced again by the API. */
export class ReservationsService {
  static async getPendingCount(r: number, b: number): Promise<number> {
    const res = await axios.get<{ pending: number }>(
      `${apiUrl}/pending-count?${queryString({ r, b })}`
    );
    return res.data.pending;
  }

  static async getBookingContext(
    r: number,
    b: number
  ): Promise<IReservationBookingContext> {
    const res = await axios.get<IReservationBookingContext>(
      `${apiUrl}/booking-context?${queryString({ r, b })}`
    );
    return res.data;
  }

  static async getDay(
    r: number,
    b: number,
    date: string
  ): Promise<IReservationsDay> {
    const res = await axios.get<IReservationsDay>(
      `${apiUrl}/day?${queryString({ r, b, date })}`
    );
    return res.data;
  }

  static async getUpcoming(
    r: number,
    b: number,
    withinMin = 90
  ): Promise<IReservationsUpcoming> {
    const res = await axios.get<IReservationsUpcoming>(
      `${apiUrl}/upcoming?${queryString({ r, b, withinMin })}`
    );
    return res.data;
  }

  static async getSlots(
    r: number,
    b: number,
    date: string,
    party: number,
    options: IReservationSlotsOptions = {}
  ): Promise<IReservationSlots> {
    const res = await axios.get<IReservationSlots>(
      `${apiUrl}/slots?${queryString({
        r,
        b,
        date,
        party,
        excludeReservationId: options.excludeReservationId,
        durationMin: options.durationMin,
      })}`
    );
    return res.data;
  }

  /** Inclusive business-date range; the server returns 50 rows per page. */
  static async getRange(
    r: number,
    b: number,
    from: string,
    to: string,
    filters: IReservationRangeFilters = {},
    page = 1
  ): Promise<IReservationsRange> {
    const res = await axios.get<IReservationsRange>(
      `${apiUrl}/range?${queryString({
        r,
        b,
        from,
        to,
        status: filters.status,
        source: filters.source,
        search: filters.search?.trim() || undefined,
        page,
      })}`
    );
    return res.data;
  }

  static async getOne(id: number): Promise<IReservationDetail> {
    const res = await axios.get<IReservationDetail>(`${apiUrl}/${id}`);
    return res.data;
  }

  static async create(
    dto: ICreateReservationDto
  ): Promise<ICreateReservationResult> {
    const res = await axios.post<ICreateReservationResult>(apiUrl, dto);
    return res.data;
  }

  static async update(
    id: number,
    dto: IUpdateReservationDto
  ): Promise<IReservationDetail> {
    const res = await axios.put<IReservationDetail>(`${apiUrl}/${id}`, dto);
    return res.data;
  }

  static async setStatus(
    id: number,
    dto: IChangeReservationStatusDto
  ): Promise<IReservationDetail> {
    const res = await axios.post<IReservationDetail>(
      `${apiUrl}/${id}/status`,
      dto
    );
    return res.data;
  }

  /** Staff confirm an automatically closed booking really was a no-show. */
  static async confirmNoShow(id: number): Promise<IReservationDetail> {
    const res = await axios.post<IReservationDetail>(`${apiUrl}/${id}/no-show`);
    return res.data;
  }

  static async setTables(
    id: number,
    dto: ISetReservationTablesDto
  ): Promise<IReservationDetail> {
    const res = await axios.post<IReservationDetail>(
      `${apiUrl}/${id}/tables`,
      dto
    );
    return res.data;
  }

  static async seat(
    id: number,
    dto: ISeatReservationDto = {}
  ): Promise<ISeatReservationResult> {
    const res = await axios.post<ISeatReservationResult>(
      `${apiUrl}/${id}/seat`,
      dto
    );
    return res.data;
  }

  static async resend(
    id: number,
    template: 'confirmed' | 'deposit_pending'
  ): Promise<IReservationActionResult> {
    const res = await axios.post<IReservationActionResult>(
      `${apiUrl}/${id}/resend`,
      { template }
    );
    return res.data;
  }

  static async refundDeposit(
    id: number,
    amount?: number
  ): Promise<IReservationDetail> {
    const res = await axios.post<IReservationDetail>(
      `${apiUrl}/${id}/deposit/refund`,
      amount === undefined ? {} : { amount }
    );
    return res.data;
  }

  static async applyDeposit(
    id: number,
    dto: IApplyReservationDepositDto
  ): Promise<IApplyReservationDepositResult> {
    const res = await axios.post<IApplyReservationDepositResult>(
      `${apiUrl}/${id}/deposit/apply`,
      dto
    );
    return res.data;
  }

  static async keepDeposit(id: number): Promise<IReservationDetail> {
    const res = await axios.post<IReservationDetail>(
      `${apiUrl}/${id}/deposit/keep`
    );
    return res.data;
  }

  static async waiveDeposit(id: number): Promise<IReservationDetail> {
    const res = await axios.post<IReservationDetail>(
      `${apiUrl}/${id}/deposit/waive`
    );
    return res.data;
  }

  static async createWalkIn(
    dto: ICreateReservationWalkInDto
  ): Promise<ISeatReservationResult> {
    const res = await axios.post<ISeatReservationResult>(
      `${apiUrl}/walk-ins`,
      dto
    );
    return res.data;
  }

  static async getWaitlist(
    r: number,
    b: number
  ): Promise<IReservationWaitlist[]> {
    const res = await axios.get<IReservationWaitlist[]>(
      `${apiUrl}/waitlist?${queryString({ r, b })}`
    );
    return res.data;
  }

  static async addWaitlist(
    dto: ICreateReservationWaitlistDto
  ): Promise<IReservationWaitlist> {
    const res = await axios.post<IReservationWaitlist>(
      `${apiUrl}/waitlist`,
      dto
    );
    return res.data;
  }

  static async updateWaitlist(
    id: number,
    dto: IUpdateReservationWaitlistDto
  ): Promise<IReservationWaitlist> {
    const res = await axios.put<IReservationWaitlist>(
      `${apiUrl}/waitlist/${id}`,
      dto
    );
    return res.data;
  }

  static async notifyWaitlist(id: number): Promise<IReservationWaitlist> {
    const res = await axios.post<IReservationWaitlist>(
      `${apiUrl}/waitlist/${id}/notify`
    );
    return res.data;
  }

  static async seatWaitlist(
    id: number,
    dto: ISeatReservationWaitlistDto
  ): Promise<ISeatReservationWaitlistResult> {
    const res = await axios.post<ISeatReservationWaitlistResult>(
      `${apiUrl}/waitlist/${id}/seat`,
      dto
    );
    return res.data;
  }

  static async cancelWaitlist(id: number): Promise<IReservationWaitlist> {
    const res = await axios.post<IReservationWaitlist>(
      `${apiUrl}/waitlist/${id}/cancel`
    );
    return res.data;
  }

  /** Restaurant-wide guest book, 20 rows per page. */
  static async searchGuests(
    r: number,
    filters: IReservationGuestFilters = {}
  ): Promise<IReservationGuests> {
    const res = await axios.get<IReservationGuests>(
      `${apiUrl}/guests?${queryString({
        r,
        search: filters.search?.trim() || undefined,
        page: filters.page,
        sortBy: filters.sortBy,
        sortDesc: filters.sortDesc,
      })}`
    );
    return res.data;
  }

  static async getGuest(customerId: number): Promise<IReservationGuestDetail> {
    const res = await axios.get<IReservationGuestDetail>(
      `${apiUrl}/guests/${customerId}`
    );
    return res.data;
  }

  static async updateGuest(
    customerId: number,
    dto: IUpdateReservationGuestDto
  ): Promise<IReservationGuest> {
    const res = await axios.put<IReservationGuest>(
      `${apiUrl}/guests/${customerId}`,
      dto
    );
    return res.data;
  }

  static async getStats(
    r: number,
    b: number | null,
    from: string,
    to: string
  ): Promise<IReservationStats> {
    const res = await axios.get<IReservationStats>(
      `${apiUrl}/stats?${queryString({ r, b: b ?? undefined, from, to })}`
    );
    return res.data;
  }

  static async getSettings(
    r: number,
    b: number
  ): Promise<IReservationSettingsConfiguration> {
    const res = await axios.get<
      Omit<IReservationSettingsConfiguration, 'shifts'> & {
        shifts: TShiftFromApi[];
      }
    >(`${apiUrl}/settings?${queryString({ r, b })}`);
    return { ...res.data, shifts: res.data.shifts.map(withShiftInterval) };
  }

  static async saveSettings(
    r: number,
    b: number,
    dto: IUpdateReservationSettingsDto
  ): Promise<IReservationSettings> {
    const res = await axios.put<IReservationSettings>(
      `${apiUrl}/settings?${queryString({ r, b })}`,
      dto
    );
    return res.data;
  }

  /** Shifts pass through `withShiftInterval`: older API builds omit the interval. */
  static async getShifts(r: number, b: number): Promise<IReservationShift[]> {
    const res = await axios.get<TShiftFromApi[]>(
      `${apiUrl}/shifts?${queryString({ r, b })}`
    );
    return res.data.map(withShiftInterval);
  }

  static async createShift(
    dto: IReservationShiftDto
  ): Promise<IReservationShift> {
    const res = await axios.post<TShiftFromApi>(`${apiUrl}/shifts`, dto);
    return withShiftInterval(res.data);
  }

  /** Updates require the complete shift DTO, including restaurantId and branchId. */
  static async updateShift(
    id: number,
    dto: IReservationShiftDto
  ): Promise<IReservationShift> {
    const res = await axios.put<TShiftFromApi>(`${apiUrl}/shifts/${id}`, dto);
    return withShiftInterval(res.data);
  }

  static async deleteShift(id: number): Promise<IReservationShift[]> {
    const res = await axios.delete<TShiftFromApi[]>(`${apiUrl}/shifts/${id}`);
    return res.data.map(withShiftInterval);
  }

  static async getOverrides(
    r: number,
    b: number,
    from: string,
    to: string
  ): Promise<IReservationDateOverride[]> {
    const res = await axios.get<IReservationDateOverride[]>(
      `${apiUrl}/overrides?${queryString({ r, b, from, to })}`
    );
    return res.data;
  }

  static async upsertOverride(
    r: number,
    b: number,
    date: string,
    dto: IReservationOverrideDto
  ): Promise<IReservationDateOverride> {
    const res = await axios.put<IReservationDateOverride>(
      `${apiUrl}/overrides/date/${encodeURIComponent(date)}?${queryString({
        r,
        b,
      })}`,
      dto
    );
    return res.data;
  }

  static async deleteOverride(id: number): Promise<IReservationActionResult> {
    const res = await axios.delete<IReservationActionResult>(
      `${apiUrl}/overrides/${id}`
    );
    return res.data;
  }

  static async getTableGroups(
    r: number,
    b: number
  ): Promise<IReservationTableGroup[]> {
    const res = await axios.get<IReservationTableGroup[]>(
      `${apiUrl}/table-groups?${queryString({ r, b })}`
    );
    return res.data;
  }

  static async createTableGroup(
    dto: IReservationTableGroupDto
  ): Promise<IReservationTableGroup> {
    const res = await axios.post<IReservationTableGroup>(
      `${apiUrl}/table-groups`,
      dto
    );
    return res.data;
  }

  static async updateTableGroup(
    id: number,
    dto: IReservationTableGroupDto
  ): Promise<IReservationTableGroup> {
    const res = await axios.put<IReservationTableGroup>(
      `${apiUrl}/table-groups/${id}`,
      dto
    );
    return res.data;
  }

  static async deleteTableGroup(id: number): Promise<IReservationActionResult> {
    const res = await axios.delete<IReservationActionResult>(
      `${apiUrl}/table-groups/${id}`
    );
    return res.data;
  }

  /** One request for every table the setup guide or the tables card changed. */
  static async saveTables(dto: IReservationTablesBatchDto): Promise<ITable[]> {
    const res = await axios.put<ITable[]>(`${apiUrl}/tables`, dto);
    return res.data;
  }
}
